import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { formatDate } from "@/lib/dateUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Search,
  ArrowUpDown,
  Filter,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Circle,
  ChevronDown,
  ChevronRight,
  FolderOpen,
} from "lucide-react";
import { useTasksQuery } from "@/hooks/queries/useTasksQuery";
import type { Task } from "@/hooks/useTasks";

type SortField = "assignee" | "title" | "status" | "priority" | "dueDate" | "projectName" | "workstream";
type SortDirection = "asc" | "desc";

const Tasks = () => {
  const { language } = useLanguage();
  const tz = useUserTimezone();
  const { t } = useTranslation(language);
  const navigate = useNavigate();
  const { data: allTasks = [], isLoading } = useTasksQuery();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [workstreamFilter, setWorkstreamFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("assignee");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [expandedAssignees, setExpandedAssignees] = useState<Set<string>>(new Set());

  // Get unique values for filters
  const uniqueAssignees = [...new Set(allTasks.map(task => task.assignee))].sort();
  const uniqueProjects = [...new Set(allTasks.map(task => task.projectName))].sort();
  const uniqueWorkstreams = [...new Set(allTasks.map(task => task.workstream))].sort();

  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    let result = [...allTasks];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        task =>
          task.title.toLowerCase().includes(query) ||
          task.description.toLowerCase().includes(query) ||
          task.assignee.toLowerCase().includes(query) ||
          task.projectName.toLowerCase().includes(query)
      );
    }

    // Apply filters
    if (statusFilter !== "all") {
      result = result.filter(task => task.status === statusFilter);
    }
    if (priorityFilter !== "all") {
      result = result.filter(task => task.priority === priorityFilter);
    }
    if (assigneeFilter !== "all") {
      result = result.filter(task => task.assignee === assigneeFilter);
    }
    if (projectFilter !== "all") {
      result = result.filter(task => task.projectName === projectFilter);
    }
    if (workstreamFilter !== "all") {
      result = result.filter(task => task.workstream === workstreamFilter);
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison: number;
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (sortField === "dueDate") {
        comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
      } else if (sortField === "priority") {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
      } else if (sortField === "status") {
        const statusOrder = { todo: 1, in_progress: 2, review: 3, done: 4 };
        comparison = statusOrder[a.status] - statusOrder[b.status];
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [allTasks, searchQuery, statusFilter, priorityFilter, assigneeFilter, projectFilter, workstreamFilter, sortField, sortDirection]);

  // Group tasks by assignee
  const tasksByAssignee = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    filteredTasks.forEach(task => {
      if (!grouped[task.assignee]) {
        grouped[task.assignee] = [];
      }
      grouped[task.assignee].push(task);
    });
    return grouped;
  }, [filteredTasks]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const toggleAssignee = (assignee: string) => {
    setExpandedAssignees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignee)) {
        newSet.delete(assignee);
      } else {
        newSet.add(assignee);
      }
      return newSet;
    });
  };

  const handleRowDoubleClick = (projectId: string) => {
    navigate(`/project/${projectId}`);
  };

  const getStatusIcon = (status: Task["status"]) => {
    switch (status) {
      case "todo":
        return <Circle className="h-4 w-4 text-muted-foreground" />;
      case "in_progress":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "review":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case "done":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    }
  };

  const getStatusLabel = (status: Task["status"]) => {
    switch (status) {
      case "todo":
        return t("taskTodo");
      case "in_progress":
        return t("taskInProgress");
      case "review":
        return t("taskReview");
      case "done":
        return t("taskDone");
    }
  };

  const getPriorityBadge = (priority: Task["priority"]) => {
    switch (priority) {
      case "low":
        return <Badge variant="secondary">{t("low")}</Badge>;
      case "medium":
        return <Badge className="bg-yellow-500 hover:bg-yellow-600 text-white">{t("medium")}</Badge>;
      case "high":
        return <Badge variant="destructive">{t("high")}</Badge>;
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase();
  };

  const getTaskStats = (tasks: Task[]) => {
    const todo = tasks.filter(t => t.status === "todo").length;
    const inProgress = tasks.filter(t => t.status === "in_progress").length;
    const review = tasks.filter(t => t.status === "review").length;
    const done = tasks.filter(t => t.status === "done").length;
    return { todo, inProgress, review, done, total: tasks.length };
  };

  const totalStats = getTaskStats(filteredTasks);

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={() => handleSort(field)}
    >
      {children}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">{t("tasks")}</h1>
          <p className="text-muted-foreground mt-1">
            {language === "sv" ? "Översikt av alla uppgifter från alla projekt" : "Overview of all tasks from all projects"}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <FolderOpen className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalStats.total}</p>
                  <p className="text-sm text-muted-foreground">
                    {language === "sv" ? "Totalt" : "Total"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <Circle className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalStats.todo}</p>
                  <p className="text-sm text-muted-foreground">{t("taskTodo")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalStats.inProgress}</p>
                  <p className="text-sm text-muted-foreground">{t("taskInProgress")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalStats.review}</p>
                  <p className="text-sm text-muted-foreground">{t("taskReview")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalStats.done}</p>
                  <p className="text-sm text-muted-foreground">{t("taskDone")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t("search")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t("status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatuses")}</SelectItem>
                  <SelectItem value="todo">{t("taskTodo")}</SelectItem>
                  <SelectItem value="in_progress">{t("taskInProgress")}</SelectItem>
                  <SelectItem value="review">{t("taskReview")}</SelectItem>
                  <SelectItem value="done">{t("taskDone")}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={language === "sv" ? "Prioritet" : "Priority"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === "sv" ? "Alla prioriteter" : "All Priorities"}</SelectItem>
                  <SelectItem value="high">{t("high")}</SelectItem>
                  <SelectItem value="medium">{t("medium")}</SelectItem>
                  <SelectItem value="low">{t("low")}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={language === "sv" ? "Tilldelad" : "Assignee"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === "sv" ? "Alla användare" : "All Users"}</SelectItem>
                  {uniqueAssignees.map(assignee => (
                    <SelectItem key={assignee} value={assignee}>{assignee}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder={t("project")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === "sv" ? "Alla projekt" : "All Projects"}</SelectItem>
                  {uniqueProjects.map(project => (
                    <SelectItem key={project} value={project}>{project}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={workstreamFilter} onValueChange={setWorkstreamFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder={t("workstream")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allWorkstreams")}</SelectItem>
                  {uniqueWorkstreams.map(ws => (
                    <SelectItem key={ws} value={ws}>{ws}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tasks Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              {language === "sv" ? "Uppgifter per användare" : "Tasks by User"}
              <span className="text-sm font-normal text-muted-foreground">
                ({filteredTasks.length} {language === "sv" ? "uppgifter" : "tasks"})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>
                      <SortableHeader field="assignee">
                        {language === "sv" ? "Tilldelad" : "Assignee"}
                      </SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="title">
                        {language === "sv" ? "Uppgift" : "Task"}
                      </SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="projectName">
                        {t("project")}
                      </SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="workstream">
                        {t("workstream")}
                      </SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="status">
                        {t("status")}
                      </SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="priority">
                        {language === "sv" ? "Prioritet" : "Priority"}
                      </SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="dueDate">
                        {language === "sv" ? "Förfallodatum" : "Due Date"}
                      </SortableHeader>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(tasksByAssignee).map(([assignee, tasks]) => {
                    const stats = getTaskStats(tasks);
                    const isExpanded = expandedAssignees.has(assignee);
                    
                    return (
                      <React.Fragment key={assignee}>
                        <TableRow 
                          className="bg-muted/50 hover:bg-muted cursor-pointer font-medium"
                          onClick={() => toggleAssignee(assignee)}
                        >
                          <TableCell>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                  {getInitials(assignee)}
                                </AvatarFallback>
                              </Avatar>
                              <span>{assignee}</span>
                            </div>
                          </TableCell>
                          <TableCell colSpan={5}>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-muted-foreground">{stats.total} {language === "sv" ? "uppgifter" : "tasks"}</span>
                              <div className="flex items-center gap-2">
                                <span className="flex items-center gap-1">
                                  <Circle className="h-3 w-3 text-muted-foreground" />
                                  {stats.todo}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3 text-blue-500" />
                                  {stats.inProgress}
                                </span>
                                <span className="flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3 text-yellow-500" />
                                  {stats.review}
                                </span>
                                <span className="flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                                  {stats.done}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                        {isExpanded && tasks.map((task) => (
                          <TableRow 
                            key={task.id}
                            className="cursor-pointer hover:bg-accent/50"
                            onDoubleClick={() => handleRowDoubleClick(task.projectId)}
                            title={language === "sv" ? "Dubbelklicka för att öppna projektet" : "Double-click to open project"}
                          >
                            <TableCell></TableCell>
                            <TableCell className="pl-12">
                              <span className="text-muted-foreground text-sm">
                                {assignee.split(" ")[0]}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{task.title}</p>
                                <p className="text-sm text-muted-foreground">{task.description}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-normal">
                                {task.projectName}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{task.workstream}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getStatusIcon(task.status)}
                                <span className="text-sm">{getStatusLabel(task.status)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {getPriorityBadge(task.priority)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                {formatDate(task.dueDate, tz, language)}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {filteredTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="h-24 text-center">
                        {t("noResults")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <p className="text-sm text-muted-foreground mt-4 flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              {language === "sv" 
                ? "Dubbelklicka på en rad för att navigera till projektet" 
                : "Double-click on a row to navigate to the project"}
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Tasks;
